import { useEffect, useState, type FormEvent } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  adminClient,
  type ServerConfigClaudePayload,
  type ServerConfigResponse,
} from '../../services/adminClient'
import { EmailTab } from './EmailTab'

type TabKey = 'claude' | 'email' | 'general'

const CLAUDE_MODELS = [
  'claude-opus-4-7',
  'claude-sonnet-4-6',
  'claude-haiku-4-5-20251001',
  'claude-sonnet-4-5',
  'claude-opus-4-5',
  'claude-haiku-4-5',
]

/**
 * Configuración del servidor (pendientes #2 y #3 del backlog).
 *
 *   - Tab "Claude AI" (pendiente #3): editar API key y system prompt.
 *   - Tab "Envío de correo" (pendiente #2): por implementar — placeholder.
 *   - Tab "General": por implementar — placeholder.
 *
 * Solo accesible desde el portal admin (ya protegido por AdminGuard en el router).
 */
export function ServerConfigPage() {
  const [tab, setTab] = useState<TabKey>('claude')

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Configuración del servidor</h1>
          <span className="muted">Solo accesible para administradores de plataforma</span>
        </div>
      </header>

      <div className="tabs">
        <TabButton active={tab === 'claude'} onClick={() => setTab('claude')}>Claude AI</TabButton>
        <TabButton active={tab === 'email'} onClick={() => setTab('email')}>Envío de correo</TabButton>
        <TabButton active={tab === 'general'} onClick={() => setTab('general')}>General</TabButton>
      </div>

      {tab === 'claude' && <ClaudeTab />}
      {tab === 'email' && <EmailTab />}
      {tab === 'general' && <PlaceholderTab title="General" pending="futuro" />}
    </div>
  )
}

function TabButton({
  active, onClick, children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      className={active ? 'tab-button active' : 'tab-button'}
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </button>
  )
}

function PlaceholderTab({ title, pending }: { title: string; pending: string }) {
  return (
    <section className="card">
      <h3>{title}</h3>
      <p className="muted">
        Esta sección se implementa en un milestone próximo (pendiente <strong>{pending}</strong> del
        backlog).
      </p>
    </section>
  )
}

function ClaudeTab() {
  const [state, setState] = useState<ServerConfigResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')

  const [showApiKey, setShowApiKey] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  function hydrate(s: ServerConfigResponse) {
    setState(s)
    setApiKey('')   // siempre vacío — para reemplazar hay que escribir el valor completo nuevo
    setModel(s.claude.model.value ?? '')
    setSystemPrompt(s.claude.system_prompt.value ?? '')
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    adminClient.serverConfig
      .show()
      .then((s) => { if (!cancelled) hydrate(s) })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar.')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSavedMessage(null)
    setSaveError(null)
    setSubmitting(true)

    // Solo enviamos los campos que cambiaron explícitamente.
    const payload: ServerConfigClaudePayload = {}
    if (apiKey.trim() !== '') payload.api_key = apiKey.trim()
    if (model !== (state?.claude.model.value ?? '')) payload.model = model
    if (systemPrompt !== (state?.claude.system_prompt.value ?? '')) payload.system_prompt = systemPrompt

    if (Object.keys(payload).length === 0) {
      setSavedMessage('Sin cambios pendientes.')
      setSubmitting(false)
      return
    }

    try {
      const res = await adminClient.serverConfig.updateClaude(payload)
      hydrate(res)
      setSavedMessage(`Guardado · cambió: ${res.changed.join(', ')}`)
      setTimeout(() => setSavedMessage(null), 3500)
    } catch (err) {
      setSaveError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loadError) return <section className="card"><p className="error">{loadError}</p></section>
  if (loading || !state) return <section className="card"><p className="muted">Cargando…</p></section>

  return (
    <section className="card">
      <h3>Claude AI</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Configura las credenciales y el comportamiento del servicio de mejora de redacción.
        Los valores en blanco usan los defaults del .env del servidor.
      </p>

      <form className="form-grid" onSubmit={handleSubmit} noValidate style={{ marginTop: '1rem' }}>
        <label className="field">
          <span>API Key</span>
          {state.claude.api_key.is_set && (
            <small className="muted">
              Actualmente configurada: <span className="mono">{state.claude.api_key.value}</span>
            </small>
          )}
          <div className="password-row">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={state.claude.api_key.is_set ? 'Dejar en blanco para mantener la actual' : 'sk-ant-…'}
              disabled={submitting}
              className="mono"
              autoComplete="off"
            />
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowApiKey((v) => !v)}
              aria-label={showApiKey ? 'Ocultar' : 'Mostrar'}
            >
              {showApiKey ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </label>

        <label className="field">
          <span>Modelo <em className="muted">(en blanco usa el default)</em></span>
          <input
            list="claude-models-list"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={state.claude.default_model}
            disabled={submitting}
            className="mono"
          />
          <datalist id="claude-models-list">
            {CLAUDE_MODELS.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <small className="muted">Default actual: <span className="mono">{state.claude.default_model}</span></small>
        </label>

        <label className="field">
          <span>System Prompt <em className="muted">(en blanco usa el default)</em></span>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={14}
            placeholder="Personaliza el comportamiento del modelo. En blanco se usa el system prompt default optimizado para reportes de servicio técnico."
            disabled={submitting}
            style={{ fontFamily: "'SF Mono', Menlo, monospace", fontSize: '.85rem' }}
          />
          <details style={{ marginTop: '.4rem' }}>
            <summary className="muted" style={{ cursor: 'pointer' }}>Ver system prompt default</summary>
            <pre style={{
              background: 'var(--bg-2)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '.85rem',
              fontSize: '.78rem',
              whiteSpace: 'pre-wrap',
              maxHeight: 320,
              overflow: 'auto',
              marginTop: '.4rem',
            }}>
              {state.claude.default_system_prompt}
            </pre>
          </details>
        </label>

        {saveError && <p className="error">{saveError}</p>}
        {savedMessage && <p className="info">{savedMessage}</p>}

        <div className="modal__actions">
          <button type="submit" className="primary-btn" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </section>
  )
}
